import {defineStore} from 'pinia'
import {ref, computed, Ref} from 'vue'
import type {components} from '@/types/accus.d'
import type {
  AccusStepOne,
  AccusStepTwo,
  AccusStepThree,
  AccusStepFour,
  AccusStepFive,
  RequestBody
} from '@/types/accumulator-selector-store.d'
import {useAccumulatorSelectorService} from '@/composables/accumulator_selector_service'
import {useProductService} from '@/composables/product_service'
import {PROPERTY_SCOPE_NAMES} from '~/assets/js/constants'
import useI18nExtended from '~/composables/usei18nExtended'
import {useGtm} from '@gtm-support/vue-gtm'

export const useAccumulatorSelectorStore = defineStore('AccumulatorSelectorModule', () => {
  const {getProductsBySkusNew} = useProductService()
  const app = useNuxtApp()
  const i18n = useI18nExtended()

  // Initial States
  const initialStepOne: AccusStepOne = {
    applicationArea: '',
    operatingFluid: 'mineralOil',
    customApplicationArea: ''
  }

  const initialStepTwo: AccusStepTwo = {
    maxOperatingPressure: null,
    minOperatingPressure: null,
    prechargePressure: null,
    isBar: true,
    prevailingTemperatures: 'standard',
    isCelsius: true,
    custom: {
      minOilTemperature: null,
      maxOilTemperature: null,
      minAmbientTemperature: null,
      maxAmbientTemperature: null
    }
  }

  const initialStepThree: AccusStepThree = {
    gasVolume: null,
    isRefillable: true,
    refillableFstStandard: 'refillable-fst-standard',
    isLiter: true
  }

  const initialStepFour: AccusStepFour = {
    isOilPortFstStandard: true,
    subOption: 'g12IG',
    custom: {
      externalThreadAg: '',
      internalThreadIg: '',
      hexagon: null,
      isMetric: true
    }
  }

  const initialStepFive: AccusStepFive = {
    mountedAccumulator: 'vertical',
    maxDiameter: null,
    maxHeight: null,
    isMetric: true
  }

  // State
  const metadata: Ref<components['schemas']['ApiAccumulatorMetaData']> = ref({})
  const currentData: Ref<RequestBody> = ref({})
  const {getMetadata: fetchMetadata} = useAccumulatorSelectorService()
  const itemCount = ref(0)
  const results = ref(null)
  const currentStep: Ref<number> = ref(1)
  const completedSteps: Ref<number[]> = ref([])
  const accumulatorSelectorService = useAccumulatorSelectorService()

  const accumulatorSelectorStepOne: Ref<AccusStepOne> = ref({...initialStepOne})
  const accumulatorSelectorStepTwo: Ref<AccusStepTwo> = ref({...initialStepTwo})
  const accumulatorSelectorStepThree: Ref<AccusStepThree> = ref({...initialStepThree})
  const accumulatorSelectorStepFour: Ref<AccusStepFour> = ref({...initialStepFour})
  const accumulatorSelectorStepFive: Ref<AccusStepFive> = ref({...initialStepFive})

  // Getters 
  const getMetadata = async () => {
    try {
      const data = await fetchMetadata()
      setMetadata(data)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch metadata', e)
      setMetadata({})
    }
  }

  const combinedData = computed<components['schemas']['ApiPostProductRequest']>(() => ({
    pressureUnit: completedSteps.value.includes(2) ? (accumulatorSelectorStepTwo.value.isBar ? 'bar' : 'psi') : 'bar',
    maxPressure: completedSteps.value.includes(2) ? accumulatorSelectorStepTwo.value.maxOperatingPressure : null,
    prevailingTemperature: completedSteps.value.includes(2) ? accumulatorSelectorStepTwo.value.prevailingTemperatures : null,
    gasVolumeUnit: completedSteps.value.includes(3) ?  (accumulatorSelectorStepThree.value.isLiter ? 'l' : 'gal') : 'l',
    gasVolume: completedSteps.value.includes(3) ? accumulatorSelectorStepThree.value.gasVolume : null,
    gasPortIsRefillable: completedSteps.value.includes(3) ? accumulatorSelectorStepThree.value.isRefillable : null,
    oilPortIG: completedSteps.value.includes(4) ? (accumulatorSelectorStepFour.value.isOilPortFstStandard ? (accumulatorSelectorStepFour.value.subOption === 'g12IG' || accumulatorSelectorStepFour.value.subOption === 'g12AG' ? 'A' : 'C' ) : null) : null,
    oilPortAG: completedSteps.value.includes(4) ? (accumulatorSelectorStepFour.value.isOilPortFstStandard ? (accumulatorSelectorStepFour.value.subOption === 'g12AG' ? 'A' : (accumulatorSelectorStepFour.value.subOption === 'g34AG' ? 'D' : null)) : null) : null,
    diameterUnit: completedSteps.value.includes(5) ? (accumulatorSelectorStepFive.value.isMetric ? 'mm' : 'inch') : 'mm',
    maxDiameter: completedSteps.value.includes(5) ? accumulatorSelectorStepFive.value.maxDiameter : null
  }
  ))

  const sendDataToBackend = async () => {
    const requestBody = combinedData.value
    const response = await accumulatorSelectorService.postAccumulatorSelectorValues(requestBody)

    useGtm()?.trackEvent({
      event: 'accus_guided_step' + currentStep.value,
      category: 'accumulator_selector',
      action: 'result_count',
      ecommerce: {
        'accus_guided_application_area': accumulatorSelectorStepOne.value.applicationArea,
        'accus_guided_precharge_pressure': accumulatorSelectorStepTwo.value.prechargePressure,
        'accus_guided_min_operating_pressure': accumulatorSelectorStepTwo.value.minOperatingPressure,
        'accus_guided_max_operating_pressure': accumulatorSelectorStepTwo.value.maxOperatingPressure,
        'accus_guided_nominal_gasvolume': accumulatorSelectorStepThree.value.gasVolume,
        'accus_guided_operating_fluid': accumulatorSelectorStepOne.value.operatingFluid,
        'accus_guided_gas_port': accumulatorSelectorStepThree.value.isRefillable ? accumulatorSelectorStepThree.value.refillableFstStandard : 'non-refillable',
        'accus_guided_internal_thread_ig': accumulatorSelectorStepFour.value.custom.internalThreadIg,
        'accus_guided_external_thread_ag': accumulatorSelectorStepFour.value.custom.externalThreadAg,
        'accus_guided_hexagon_e': accumulatorSelectorStepFour.value.custom.hexagon,
        'accus_guided_max_diameter': accumulatorSelectorStepFive.value.maxDiameter,
        'accus_guided_max_height': accumulatorSelectorStepFive.value.maxHeight
      }
    })

    if (response && response.products) {
      itemCount.value = response.products.length
      const skuList = [...new Set(response.products.map(p => p.sku))]
      if (currentStep.value === 6) {
        const sketches = await getProductsBySkusNew(skuList, app.$globalization.getRegion(), i18n.locale.value, PROPERTY_SCOPE_NAMES.HYDRAULIC_SELECTOR_NEW, null, true)
        const last100 = response.products.length - 1 - response.products.slice().reverse().findIndex(p => p.matchingScore === 100)
        results.value = response.products.map((p, i) => ({...p, designSketchUri: sketches?.find(s => s?.sku === p?.sku)?.designSketchUri, last100: i === last100}))
      } else {
        results.value = response.products
      }
    } else {
      itemCount.value = 0
    }
    return response
  }

  const setCurrentData = (data: components['schemas']['ApiPostProductRequest']) => {
    currentData.value = data
  }

  const setMetadata = (data: components['schemas']['ApiAccumulatorMetaData']) => {
    metadata.value = data
  }

  const resetState = () => {
    currentData.value = {}
    accumulatorSelectorStepOne.value = {...initialStepOne}
    accumulatorSelectorStepTwo.value = {...initialStepTwo}
    accumulatorSelectorStepThree.value = {...initialStepThree}
    accumulatorSelectorStepFour.value = {...initialStepFour}
    accumulatorSelectorStepFive.value = {...initialStepFive}
    itemCount.value = 0
    results.value = null
    currentStep.value = 1
    completedSteps.value = []
  }

  return {
    // State
    metadata,
    currentData,
    accumulatorSelectorStepOne,
    accumulatorSelectorStepTwo,
    accumulatorSelectorStepThree,
    accumulatorSelectorStepFour,
    accumulatorSelectorStepFive,
    currentStep,
    completedSteps,
    // Getters
    combinedData,
    getMetadata,
    // setters
    sendDataToBackend,
    setMetadata,
    setCurrentData,
    results,
    itemCount,
    resetState
  }
})
